import { useNavigate } from 'react-router-dom'
import { sendAnalyticsTrack, setSendAnalyticsValue } from 'Utils/analytics.utils.ts'
import FullWidthCard, {
    CardTagPropsType,
} from 'Components/common/Cards/FullWidthCard/FullWidthCard.tsx'
import { AssignmentScopeResponseType, TaskStatusType } from 'Types/Tasks.types.ts'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useUserProfile } from 'shared/auth/hooks/use-user-profile/useUserProfile'
import { getAssignmentScopes, getStepLevelTaskDetails } from 'Services/tasks.service.ts'
import { EmptyStateIndicator } from 'Components/common/EmptyStaleIndicator/EmptyStaleIndicator.tsx'
import styles from '../../Home.module.less'
import { CardListSkeleton } from 'Components/common/CardListSkeleton/CardListSkeleton'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import ErrorIcon from 'Assets/icons/error-icon.svg'
import Skeleton from 'antd/lib/skeleton'
import { META_PROJECT_ID } from 'Constants/constants'
import { isUserAssociatedWithProject } from 'Services/projects.service'
import NoTasksImage from 'Assets/images/NoTasks.png'

const SkeletonLoading = () => (
    <div className={styles['footer-loading']}>
        {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className={styles['footer-content']}>
                <Skeleton.Input active size="small" className={styles['skeleton-input-small']} />
                <Skeleton.Input
                    active
                    size="small"
                    className={`${styles['skeleton-input-small']} ${styles['large']}`}
                />
            </div>
        ))}
    </div>
)

export const TaskQueuesTab: React.FC = () => {
    const limit = 5
    const threshold = 10
    const { userProfile } = useUserProfile()
    const navigate = useNavigate()
    const [taskDetails, setTaskDetails] = useState<Record<string, TaskStatusType[]>>({})
    const [loadingDetails, setLoadingDetails] = useState<Record<string, boolean>>({})
    const containerRef = useRef<HTMLDivElement | null>(null)

    const { data: userAssociationData, isLoading: isUserAssociationDataLoading } = useQuery({
        queryKey: ['userProjectAssociation', META_PROJECT_ID, userProfile.user.user_id],
        queryFn: () =>
            isUserAssociatedWithProject({
                projectId: META_PROJECT_ID,
                userId: userProfile.user.user_id,
            }),
        enabled: !!userProfile.user.user_id,
        staleTime: 3 * 60 * 1000,
    })

    useEffect(() => {
        setSendAnalyticsValue(!userAssociationData?.is_user_associated)
    }, [userAssociationData])

    const fetchAssignmentScopes = async ({
        pageParam = 0,
    }): Promise<AssignmentScopeResponseType[]> => {
        if (!userProfile.user.user_id) return []
        const resp = await getAssignmentScopes(userProfile.user.user_id, limit, pageParam)
        return resp ?? []
    }

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
        useInfiniteQuery({
            queryKey: ['assignmentScopesDetails', userProfile.user.user_id],
            queryFn: fetchAssignmentScopes,
            initialPageParam: 0,
            getNextPageParam: (lastPage, allPages) =>
                lastPage.length === limit ? allPages.flat().length : undefined,
            enabled: !isUserAssociationDataLoading,
        })

    const sendTaskTileMetrics = (
        response: AssignmentScopeResponseType,
        triggeredFromCopyButton: boolean
    ) => {
        sendAnalyticsTrack(triggeredFromCopyButton ? 'TaskTile_IDCopied' : 'TaskTile_Clicked', {
            user_id: userProfile.user.user_id,
            project_id: response.projectId,
            step_id: response.stepId,
        })
    }

    const handleAccordionToggle = async (stepId: string) => {
        if (!userProfile.user.user_id || !stepId) return

        if (taskDetails[stepId]) return
        if (loadingDetails[stepId]) return

        setLoadingDetails(prev => ({ ...prev, [stepId]: true }))

        try {
            const result = await getStepLevelTaskDetails(userProfile.user.user_id, stepId)
            setTaskDetails(prev => ({ ...prev, [stepId]: result ?? [] }))
        } catch (error) {
            console.error('Error fetching task details:', error)
        } finally {
            setLoadingDetails(prev => ({ ...prev, [stepId]: false }))
        }
    }

    const getStepParams = useCallback((stepType: string, stepName: string): CardTagPropsType => {
        const stepParams: CardTagPropsType = {
            title: stepName,
            type: 'primary',
        }
        if (stepType === 'MAKER') {
            stepParams.type = 'maker'
        }
        if (stepType === 'REVIEWER') {
            stepParams.type = 'reviewer'
        }
        if (stepType === 'TRIAGER') {
            stepParams.type = 'triager'
        }
        if (stepType === 'QUALITY_CHECK') {
            stepParams.type = 'qc'
        }
        if (stepType === 'REWORK') {
            stepParams.type = 'rework'
        }
        return stepParams
    }, [])

    const allData: AssignmentScopeResponseType[] = data?.pages.flat() ?? []

    useEffect(() => {
        const container = containerRef.current
        if (!container || isLoading) return

        const onScroll = () => {
            const atBottom =
                container.scrollTop + container.clientHeight >= container.scrollHeight - threshold

            if (atBottom && hasNextPage && !isFetchingNextPage) {
                fetchNextPage()
            }
        }

        container.addEventListener('scroll', onScroll, { passive: true })
        onScroll()

        return () => {
            container.removeEventListener('scroll', onScroll)
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading])

    if (error) {
        return (
            <div className={styles['body']}>
                <EmptyStateIndicator
                    label="Something went wrong"
                    caption={'Oops! It looks like there was an issue. Please try again.'}
                    icon={ErrorIcon}
                />
                <button
                    className={styles['retry-button']}
                    onClick={() => {
                        fetchNextPage()
                        sendAnalyticsTrack('TaskTile_RetryClicked', {
                            user_id: userProfile.user.user_id,
                        })
                    }}
                >
                    Retry
                </button>
            </div>
        )
    }

    if (isLoading) {
        return (
            <div className={styles['body']}>
                <div className={styles['card-body']}>
                    <CardListSkeleton />
                </div>
            </div>
        )
    }

    if (!isLoading && !isUserAssociationDataLoading && allData.length === 0) {
        return (
            <div className={styles['body']}>
                <div className={styles['empty-state-container']}>
                    <EmptyStateIndicator
                        label="No tasks found"
                        caption="There are currently no tasks available for you. Please check back later here for further updates."
                        icon={NoTasksImage}
                    />
                </div>
            </div>
        )
    }

    return (
        <div ref={containerRef} className={styles['scrollContainer']}>
            <div className={styles['body']}>
                <div className={styles['card-body']}>
                    {allData.map((resp: AssignmentScopeResponseType) => (
                        <div key={resp.stepId}>
                            <FullWidthCard
                                stepId={resp.stepId}
                                title={resp.projectNickname}
                                maxContentWords={50}
                                tags={[
                                    { title: resp.workflowUseCase, type: 'primary' },
                                    getStepParams(resp.stepType, resp.stepName),
                                ]}
                                content={resp.projectDescription}
                                footer={
                                    <div className={styles['footer']}>
                                        <div
                                            className={styles['footer-body']}
                                            data-testid="footer-body"
                                        >
                                            {loadingDetails[resp.stepId] ? (
                                                <SkeletonLoading />
                                            ) : (
                                                taskDetails[resp.stepId]?.map(
                                                    (task: TaskStatusType) => (
                                                        <div
                                                            className={styles['footer-content']}
                                                            key={task.label}
                                                        >
                                                            <span>{task.label}</span>
                                                            <span>{task.value}</span>
                                                        </div>
                                                    )
                                                )
                                            )}
                                        </div>
                                    </div>
                                }
                                onAccordionToggle={() => handleAccordionToggle(resp.stepId)}
                                onClick={() => {
                                    const projectId = resp.projectId
                                    navigate(`/task-queue/${resp.stepId}`, {
                                        state: { projectId },
                                    })
                                    sendTaskTileMetrics(resp, false)
                                }}
                                onCopyStepId={() => {
                                    sendTaskTileMetrics(resp, true)
                                }}
                            />
                        </div>
                    ))}
                    {isFetchingNextPage && (
                        <div className={styles['loading-more']}>
                            <CardListSkeleton />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
